import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { Reveal, ImageReveal, MaskLines } from "../lib/motion";
import { ArcButton } from "../components/site/Brand";
import { ProjectCard } from "../components/site/ProjectCard";
import { getProject, relatedProjects } from "../data/projects";

const Meta = ({ label, value }) => (
  <div className="border-t border-charcoal/15 pt-4">
    <p className="overline text-bronze mb-2">{label}</p>
    <p className="font-light">{value}</p>
  </div>
);

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = getProject(slug);
  if (!project) return <Navigate to="/projects" replace />;
  const related = relatedProjects(slug);

  return (
    <>
      <Seo
        title={`${project.title} | Arc & Hollow`}
        description={project.intro}
        path={`/projects/${slug}`}
      />

      <section className="container-x pt-36 md:pt-52 pb-12" data-testid="project-detail">
        <Link to="/projects" className="inline-flex items-center gap-2 overline text-olive hover:text-charcoal mb-10" data-testid="back-to-projects">
          <ArrowLeft size={14} strokeWidth={1.6} /> All projects
        </Link>
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <span className="bg-charcoal text-chalk px-3 py-1.5 overline">{project.status}</span>
          <span className="overline text-olive">{project.categories.join(" · ")}</span>
        </div>
        <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
          <MaskLines lines={[project.title]} />
        </h1>
        <p className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-olive">{project.intro}</p>
      </section>

      <section className="container-x pb-16">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Meta label="Location" value={project.location} />
          <Meta label="Year" value={project.year} />
          <Meta label="Status" value={project.status} />
          <Meta label="Scope" value={project.scope} />
        </div>
      </section>

      <section className="container-x pb-8">
        <div className="relative">
          <ImageReveal src={project.gallery[0].src} alt={`${project.title} — ${project.gallery[0].caption}`} className="aspect-[16/10]" />
          <p className="mt-3 text-xs uppercase tracking-overline text-olive/70">{project.gallery[0].caption}</p>
        </div>
      </section>

      <section className="container-x py-16 md:py-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <p className="overline text-bronze mb-6">Design narrative</p>
            <p className="text-lg font-light leading-relaxed text-olive">{project.narrative}</p>

            <p className="overline text-bronze mb-6 mt-12">The challenge</p>
            <p className="text-lg font-light leading-relaxed text-olive">{project.challenge}</p>

            <p className="overline text-bronze mb-6 mt-12">Approach &amp; response</p>
            <p className="text-lg font-light leading-relaxed text-olive">{project.response}</p>
          </Reveal>

          <div className="lg:col-span-5">
            <div className="bg-limestone/40 p-8 md:p-10">
              <p className="overline text-bronze mb-6">Materials palette</p>
              <ul className="space-y-3">
                {project.materials.map((m) => (
                  <li key={m} className="flex items-center gap-4 font-light border-b border-charcoal/10 pb-3">
                    <span className="h-3 w-3 rounded-full bg-bronze/70 shrink-0" />
                    {m}
                  </li>
                ))}
              </ul>
              <p className="overline text-bronze mb-3 mt-10">Intended atmosphere</p>
              <p className="font-serif text-2xl font-light italic leading-snug">{project.intendedAtmosphere}</p>
            </div>
          </div>
        </div>
      </section>

      {project.gallery.length > 1 && (
        <section className="container-x pb-16 md:pb-24">
          <div className="grid gap-8 md:grid-cols-2">
            {project.gallery.slice(1).map((g, i) => (
              <div key={i} className={g.wide ? "md:col-span-2" : ""}>
                <ImageReveal src={g.src} alt={`${project.title} — ${g.caption}`} className={g.wide ? "aspect-[16/9]" : "aspect-[4/3]"} />
                <p className="mt-3 text-xs uppercase tracking-overline text-olive/70">{g.caption}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-limestone/40 py-16 md:py-20">
        <div className="container-x flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <p className="overline text-bronze mb-4">Credits</p>
            <ul className="space-y-1 font-light text-olive">
              {project.credits.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </div>
          <ArcButton to="/contact" variant="primary" data-testid="project-enquiry">
            Enquire about a project like this <ArrowRight size={15} strokeWidth={1.6} />
          </ArcButton>
        </div>
      </section>

      {related.length > 0 && (
        <section className="container-x py-24 md:py-32">
          <p className="overline text-bronze mb-12">Related projects</p>
          <div className="grid gap-x-8 gap-y-14 md:grid-cols-2">
            {related.map((p, i) => <ProjectCard key={p.slug} project={p} index={i} />)}
          </div>
        </section>
      )}
    </>
  );
}
